<template>
  <TransitionGroup
    appear
    :class="rootElClasses"
    tag="div"
    :css="false"
    @before-enter="beforeEnter"
    @enter="enter"
    @leave="leave"
  >
    <PlayingCard
      v-for="(card, cardIdx) in cards"
      :key="cardIdx"
      :value="card"
      :lost="isCardLost(card)"
      :data-index="cardIdx"
    />
  </TransitionGroup>
</template>

<script lang="ts" setup>
import { computed, ref, nextTick, onMounted } from 'vue'
import PlayingCard from '@/components/table/PlayingCard.vue'
import { useGameState } from '@/stores/gameState'
import { useAppMode } from '@/composables/useAppMode'
import type { HoleCardsValue } from '@acepoker/contract/acepoker'

interface Props {
  cards: HoleCardsValue
  fromElSelector?: string
  isOpen: boolean
  isSelf: boolean
  isDealing: boolean
}

const TRANSITION_CARD_IN = 'transform .3s ease-in-out, opacity .3s ease-in-out'
const TRANSITION_CARD_OUT = 'all .5s ease-out'
const TRANSITION_CARD_OUT_WHILE_DEALING = 'all .2s ease-out'

const { isCardLost } = useGameState()

const props = withDefaults(defineProps<Props>(), {
  fromElSelector: '.table__total-pot'
})

const { mode: appViewMode } = useAppMode()

const fromEl = ref<HTMLElement | null>(null)

const rootElClasses = computed(() => {
  const className = 'seat-card-group'

  return [
    className,
    `${className}--${appViewMode.value}`,
    {
      [`${className}--self`]: props.isSelf,
      [`${className}--open`]: props.isOpen
    }
  ]
})

const applyStyles = (el: HTMLElement, opacity: string, transform: string, transition = 'none') => {
  el.style.opacity = opacity
  el.style.transform = transform
  el.style.transition = transition
}
const getCardRotation = (index: number) => (index === 0 ? '-6deg' : '6deg')

const beforeEnter = async (el: HTMLElement) => {
  if (!props.isDealing || !fromEl.value) return
  await nextTick()

  const fromRect = fromEl.value.getBoundingClientRect()
  const toRect = el.getBoundingClientRect()

  const offsetX = fromRect.left + fromRect.width / 2 - (toRect.left + toRect.width / 2)
  const offsetY = fromRect.top + fromRect.height / 2 - (toRect.top + toRect.height / 2)

  applyStyles(el, '0.5', `translate(${offsetX}px, ${offsetY}px) scale(1) rotate(0deg)`)
}

const enter = (el: HTMLElement, done: () => void) => {
  const cardIdx = Number(el.dataset.index || '0')
  const transform = `translate(0, 0) scale(1) rotate(${getCardRotation(cardIdx)})`

  if (!props.isDealing) {
    applyStyles(el, '1', transform)
    done()
    return
  }

  requestAnimationFrame(() => {
    applyStyles(el, '1', transform, TRANSITION_CARD_IN)
    const onEnd = () => {
      el.removeEventListener('transitionend', onEnd)
      done()
    }
    el.addEventListener('transitionend', onEnd, { once: true })
  })
}

const leave = (el: HTMLElement, done: () => void) => {
  const styles = getComputedStyle(el)
  const height = styles.getPropertyValue('--card-player-height').trim()
  const transform = `translate(0, ${height}) scale(${props.isSelf ? '0.1' : '0.7'})`
  const transition = props.isDealing ? TRANSITION_CARD_OUT_WHILE_DEALING : TRANSITION_CARD_OUT
  const opacity = props.isDealing ? '0' : '0.7'

  applyStyles(el, opacity, transform, transition)
  el.addEventListener('transitionend', done, { once: true })
}

onMounted(() => {
  fromEl.value = document.querySelector(props.fromElSelector)
})
</script>

<style lang="scss" scoped>
.seat-card-group {
  position: absolute;
  display: flex;
  flex-direction: row;
  z-index: -2;
  width: calc(var(--card-player-width) * 1.5);

  &--mobile {
    z-index: -2;
    transition:
      ease-in-out 0.5s width,
      ease-in-out 0.5s height,
      ease-in-out 0.5s left,
      ease-in-out 0.5s top,
      ease-in-out 0.5s transform;
    top: calc(3px - var(--card-player-height));
    --card-player-width: calc(var(--card-table-width) * 1.03);
    --card-player-height: calc(var(--card-table-height) * 1.03);
    &.seat-card-group--self {
      left: auto;
      right: auto;
    }
    &.seat-card-group--open {
      left: 50%;
      transform: translateX(-50%);
    }
  }

  &--desktop {
    left: 25%;
    top: -110%;
    --card-player-width: calc(var(--card-table-width) * 0.8);
    --card-player-height: calc(var(--card-table-height) * 0.8);

    &.seat-card-group--self {
      left: 15%;
      top: -125%;
      --card-player-width: calc(var(--card-table-width) * 1.1);
      --card-player-height: calc(var(--card-table-height) * 1.1);
    }
  }
}
.card {
  --card-width: var(--card-player-width);
  --card-height: var(--card-player-height);
  transition: none;
  position: absolute;
  &:first-child {
    transform: translate(0, 0) scale(1) rotate(-6deg);
    left: 0;
  }
  &:last-child {
    transform: translate(0, 0) scale(1) rotate(6deg);
    right: 0;
  }
}
</style>
